import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const hasResume = (c: any) => Boolean(c.resumeUrl || c.resume_url || c.resumeFileName || c.resume_file_name || c.resume_stored || c.resumeText || c.resume_text);
const hasLinkedIn = (c: any) => Boolean(c.linkedinUrl || c.linkedin_url || c.linkedInUrl || c.linkedInURL || c.linkedinScrape || c.linkedin_scrape || c.linkedin_stored);

const InterviewCandidates: React.FC = () => {
  const [candidates, setCandidates] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [jobFilter, setJobFilter] = useState<string>('');
  const [ratingMin, setRatingMin] = useState<string>('');
  const [ratingMax, setRatingMax] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const [cRes, jRes] = await Promise.all([
          api.get(END_POINTS.interviewCandidates, {}),
          api.get(END_POINTS.interviewJobs, {}),
        ]);
        setCandidates(Array.isArray(cRes) ? cRes : (cRes?.results || []));
        setJobs(Array.isArray(jRes) ? jRes : (jRes?.results || []));
      } catch (e: any) { ValidationErrorToast('Error', e?.message || 'Load failed'); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = candidates;

    if (jobFilter) {
      list = list.filter((c: any) => {
        const title = (c.jobTitle || c.job_title || '').toLowerCase();
        const jId = String(c.jobId || c.job_id || '');
        return jId === jobFilter || title === jobs.find((j: any) => String(j.id) === jobFilter)?.title?.toLowerCase();
      });
    }

    const min = ratingMin !== '' ? parseFloat(ratingMin) : null;
    const max = ratingMax !== '' ? parseFloat(ratingMax) : null;
    if (min !== null || max !== null) {
      list = list.filter((c: any) => {
        const raw = c.rating ?? c.score ?? null;
        if (raw === null || raw === undefined) return false;
        const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (isNaN(num)) return false;
        if (min !== null && num < min) return false;
        if (max !== null && num > max) return false;
        return true;
      });
    }

    return list;
  }, [candidates, jobFilter, ratingMin, ratingMax, jobs]);

  const downloadCsv = () => {
    const headers = ['Name', 'Email', 'Job', 'Rating', 'Status', 'Resume', 'LinkedIn', 'Report', 'Source'];
    const rows = filtered.map((c: any) => [
      (c.candidateName || c.candidate_name || '').replace(/,/g, ' '),
      (c.candidateEmail || c.candidate_email || '').replace(/,/g, ' '),
      (c.jobTitle || c.job_title || '').replace(/,/g, ' '),
      c.rating ?? c.score ?? '',
      c.status || 'pending',
      hasResume(c) ? 'yes' : 'no',
      hasLinkedIn(c) ? 'yes' : 'no',
      (c.resultId || c.result_id) ? 'yes' : 'no',
      c.source || 'invite',
    ]);
    const csv = [headers.join(','), ...rows.map((r: any) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `candidates-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading candidates...</p>
        </div>
      </InterviewSidebar>
    );
  }

  return (
    <InterviewSidebar>
      <div style={{ ...styles.card, marginBottom: '16px' }}>
        <h4 style={{ ...styles.cardTitle, fontSize: '15px', marginBottom: '12px' }}>Filters</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ minWidth: '200px', flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px', fontWeight: 500 }}>Job</label>
            <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} style={{ ...styles.searchInput, width: '100%', padding: '8px 10px', fontSize: '13px' } as any}>
              <option value="">All Jobs</option>
              {jobs.map((j: any) => <option key={j.id} value={String(j.id)}>{j.title}</option>)}
            </select>
          </div>
          <div style={{ minWidth: '120px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px', fontWeight: 500 }}>Rating Min</label>
            <input type="number" step="0.1" min="0" max="10" placeholder="0" value={ratingMin} onChange={(e) => setRatingMin(e.target.value)} style={{ ...styles.searchInput, width: '100%', padding: '8px 10px', fontSize: '13px' } as any} />
          </div>
          <div style={{ minWidth: '120px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px', fontWeight: 500 }}>Rating Max</label>
            <input type="number" step="0.1" min="0" max="10" placeholder="10" value={ratingMax} onChange={(e) => setRatingMax(e.target.value)} style={{ ...styles.searchInput, width: '100%', padding: '8px 10px', fontSize: '13px' } as any} />
          </div>
          {(jobFilter || ratingMin || ratingMax) && <button onClick={() => { setJobFilter(''); setRatingMin(''); setRatingMax(''); }} style={{ ...styles.button, ...styles.secondaryButton, padding: '8px 16px', fontSize: '13px' } as any}>Clear</button>}
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>Candidates ({filtered.length}{filtered.length !== candidates.length ? ` of ${candidates.length}` : ''})</h3>
          {filtered.length > 0 && <button style={{ ...styles.button, ...styles.secondaryButton, padding: '8px 16px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' } as any} onClick={downloadCsv}>Download CSV</button>}
        </div>
        {filtered.length === 0 ? (
          <div style={{ ...styles.emptyState, minHeight: '300px' }}>
            <p style={styles.secondaryText}>{candidates.length === 0 ? 'No candidates yet. Invite candidates from a job or branded invite page.' : 'No candidates match the current filters.'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeader, width: '18%' }}>Name</th>
                  <th style={{ ...styles.tableHeader, width: '20%' }}>Email</th>
                  <th style={{ ...styles.tableHeader, width: '16%' }}>Job</th>
                  <th style={{ ...styles.tableHeader, width: '9%', textAlign: 'center' }}>Rating</th>
                  <th style={{ ...styles.tableHeader, width: '10%', textAlign: 'center' }}>Status</th>
                  <th style={{ ...styles.tableHeader, width: '8%', textAlign: 'center' }}>Resume</th>
                  <th style={{ ...styles.tableHeader, width: '8%', textAlign: 'center' }}>LinkedIn</th>
                  <th style={{ ...styles.tableHeader, width: '11%', textAlign: 'center' }}>Report</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c: any) => {
                  const rating = c.rating ?? c.score ?? null;
                  const numRating = rating !== null ? (typeof rating === 'number' ? rating : parseFloat(String(rating))) : NaN;
                  return (
                    <tr key={c.id}>
                      <td style={styles.tableCell}><strong style={styles.primaryText}>{c.candidateName || c.candidate_name || '—'}</strong></td>
                      <td style={styles.tableCell}>{c.candidateEmail || c.candidate_email || '—'}</td>
                      <td style={styles.tableCell}>{c.jobTitle || c.job_title || '—'}</td>
                      <td style={{ ...styles.tableCell, textAlign: 'center' }}>{!isNaN(numRating) ? <span style={{ ...styles.badge, backgroundColor: numRating >= 7 ? '#28a745' : numRating >= 4 ? '#ffc107' : '#dc3545', color: numRating >= 4 && numRating < 7 ? '#212529' : '#fff' }}>{numRating}/10</span> : <span style={styles.secondaryText}>—</span>}</td>
                      <td style={{ ...styles.tableCell, textAlign: 'center' }}><span style={{ ...styles.badge, backgroundColor: c.status === 'completed' ? '#28a745' : c.status === 'started' ? '#ffc107' : '#17a2b8', color: c.status === 'started' ? '#212529' : '#ffffff' }}>{c.status || 'pending'}</span></td>
                      <td style={{ ...styles.tableCell, textAlign: 'center' }}><span style={{ ...styles.badge, backgroundColor: hasResume(c) ? '#E8F5E9' : '#F1F3F5', color: hasResume(c) ? '#1B5E20' : '#6B7280' }}>{hasResume(c) ? 'Yes' : 'No'}</span></td>
                      <td style={{ ...styles.tableCell, textAlign: 'center' }}><span style={{ ...styles.badge, backgroundColor: hasLinkedIn(c) ? '#E3F2FD' : '#F1F3F5', color: hasLinkedIn(c) ? '#0D47A1' : '#6B7280' }}>{hasLinkedIn(c) ? 'Yes' : 'No'}</span></td>
                      <td style={{ ...styles.tableCell, textAlign: 'center' }}>{c.resultId || c.result_id ? <Link to={`/interview-simulator/results/${c.resultId || c.result_id}`} style={{ ...styles.button, ...styles.secondaryButton, textDecoration: 'none', padding: '6px 12px', fontSize: '13px' } as any}>View</Link> : <span style={styles.secondaryText}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </InterviewSidebar>
  );
};

export default InterviewCandidates;
